
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useProfile } from "@/hooks/useProfile";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const ConfirmProfileLink = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { confirmProfileLink } = useProfile();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("");
  
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setStatus("error");
        setMessage("No verification token provided");
        return;
      }
      
      try {
        const success = await confirmProfileLink(token);
        
        if (success) {
          setStatus("success");
          setMessage("Profile linked successfully!");
        } else {
          setStatus("error");
          setMessage("Failed to verify profile link");
        }
      } catch (error: any) {
        setStatus("error");
        setMessage(error.message || "An unexpected error occurred");
      }
    };
    
    verifyToken();
  }, [token, confirmProfileLink]);
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full p-8 bg-white rounded-lg shadow-md text-center">
        {status === "loading" && (
          <>
            <Loader2 className="mx-auto h-16 w-16 text-primary animate-spin mb-4" />
            <h1 className="text-2xl font-bold mb-2">Verifying Link</h1>
            <p className="text-gray-600 mb-4">Please wait while we verify your profile link...</p>
          </>
        )}
        
        {status === "success" && (
          <>
            <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
            <h1 className="text-2xl font-bold mb-2">Link Confirmed!</h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <Button onClick={() => navigate("/settings")}>
              Go to Admin Dashboard
            </Button>
          </>
        )}
        
        {status === "error" && (
          <>
            <XCircle className="mx-auto h-16 w-16 text-red-500 mb-4" />
            <h1 className="text-2xl font-bold mb-2">Verification Failed</h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <Button variant="outline" onClick={() => navigate("/settings")}>
              Return to Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default ConfirmProfileLink;
